import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Stack,
  CircularProgress,
  Tabs,
  Tab,
  Popover,
  Paper,
  Grid,
  Tooltip,
} from '@mui/material';
import { Plus, Pencil, Trash2, Palette } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { departmentService, userService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import { User, UserRole } from '@/types';

const ROLE_OPTIONS: { value: UserRole; label: string }[] = [
  { value: UserRole.ADMIN, label: 'Admin' },
  { value: UserRole.MANAGER, label: 'Manager' },
  { value: UserRole.DEPARTMENT_HEAD, label: 'Department Head' },
  { value: UserRole.PROJECT_MANAGER, label: 'Project Manager' },
  { value: UserRole.TEAM_LEAD, label: 'Team Lead' },
  { value: UserRole.ENGINEER, label: 'Engineer' },
  { value: UserRole.INDIVIDUAL_CONTRIBUTOR, label: 'Individual Contributor' },
];

const DEFAULT_COLORS = [
  { hex: '#3f51b5', name: 'Indigo' },
  { hex: '#2196f3', name: 'Blue' },
  { hex: '#00bcd4', name: 'Cyan' },
  { hex: '#009688', name: 'Teal' },
  { hex: '#4caf50', name: 'Green' },
  { hex: '#ff9800', name: 'Orange' },
  { hex: '#f44336', name: 'Red' },
  { hex: '#9c27b0', name: 'Purple' },
  { hex: '#e91e63', name: 'Pink' },
  { hex: '#607d8b', name: 'Blue Grey' },
];

const buildDisplayName = (firstName: string, lastName: string) =>
  `${firstName} ${lastName}`.trim();

const UsersPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Department-related state
  const [deptCreateOpen, setDeptCreateOpen] = useState(false);
  const [deptEditOpen, setDeptEditOpen] = useState(false);
  const [deptDeleteOpen, setDeptDeleteOpen] = useState(false);
  const [selectedDept, setSelectedDept] = useState<any | null>(null);
  const [colorPickerAnchor, setColorPickerAnchor] = useState<HTMLElement | null>(null);
  const [customColorMode, setCustomColorMode] = useState(false);
  const [activeColorField, setActiveColorField] = useState<'create' | 'edit' | null>(null);
  const [deptForm, setDeptForm] = useState({
    name: '',
    headId: '',
    color: '',
  });
  const [deptEditForm, setDeptEditForm] = useState({
    name: '',
    headId: '',
    color: '',
  });

  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);
  const [form, setForm] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    displayName: '',
    role: UserRole.ENGINEER as UserRole,
    departmentId: '',
    isActive: true,
  });
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    displayName: '',
    role: UserRole.ENGINEER as UserRole,
    departmentId: '',
    isActive: true,
  });

  const { data: departments = [] } = useQuery({
    queryKey: QUERY_KEYS.departments,
    queryFn: () => departmentService.getDepartments(),
  });

  const { data: users = [], isLoading } = useQuery({
    queryKey: [QUERY_KEYS.users, search],
    queryFn: () => {
      const term = search.trim();
      if (term.length >= 2) {
        return userService.searchUsers(term);
      }
      return userService.getUsers();
    },
    staleTime: 30000,
  });

  const departmentMap = useMemo(() => {
    const map = new Map<string, string>();
    departments.forEach((dept) => map.set(dept.id, dept.name));
    return map;
  }, [departments]);

  const createUserMutation = useMutation({
    mutationFn: (payload: {
      email: string;
      password: string;
      firstName: string;
      lastName: string;
      displayName?: string;
      role: string;
      departmentId: string;
      isActive?: boolean;
    }) => userService.createUser(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users });
      setCreateOpen(false);
      setForm({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        displayName: '',
        role: UserRole.ENGINEER,
        departmentId: '',
        isActive: true,
      });
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: {
      role?: UserRole;
      isActive?: boolean;
      firstName?: string;
      lastName?: string;
      displayName?: string;
      departmentId?: string;
    } }) => userService.updateUser(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users });
      setUpdatingUserId(null);
      setEditOpen(false);
      setSelectedUser(null);
    },
    onError: () => {
      setUpdatingUserId(null);
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => userService.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users });
      setDeleteOpen(false);
      setSelectedUser(null);
    },
  });

  // Department mutations
  const createDeptMutation = useMutation({
    mutationFn: (payload: { name: string; headId?: string; color?: string }) =>
      departmentService.createDepartment(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.departments });
      setDeptCreateOpen(false);
      setDeptForm({ name: '', headId: '', color: '' });
    },
  });

  const updateDeptMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<{ name: string; headId: string; color: string }> }) =>
      departmentService.updateDepartment(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.departments });
      setDeptEditOpen(false);
      setSelectedDept(null);
    },
  });

  const deleteDeptMutation = useMutation({
    mutationFn: (deptId: string) => departmentService.deleteDepartment(deptId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.departments });
      setDeptDeleteOpen(false);
      setSelectedDept(null);
    },
  });

  const handleDeptEditOpen = (dept: any) => {
    setSelectedDept(dept);
    setDeptEditForm({
      name: dept.name,
      headId: dept.headId || '',
      color: dept.color || '',
    });
    setDeptEditOpen(true);
  };

  const handleDeptDeleteOpen = (dept: any) => {
    setSelectedDept(dept);
    setDeptDeleteOpen(true);
  };

  const handleDeptCreate = () => {
    createDeptMutation.mutate({
      name: deptForm.name.trim(),
      headId: deptForm.headId || undefined,
      color: deptForm.color || undefined,
    });
  };

  const handleDeptUpdate = () => {
    if (!selectedDept) return;
    updateDeptMutation.mutate({
      id: selectedDept.id,
      payload: {
        name: deptEditForm.name.trim(),
        headId: deptEditForm.headId || undefined,
        color: deptEditForm.color || undefined,
      },
    });
  };

  const handleCreateUser = () => {
    const displayName = form.displayName.trim() || buildDisplayName(form.firstName, form.lastName);
    createUserMutation.mutate({
      email: form.email.trim(),
      password: form.password,
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      displayName,
      role: form.role,
      departmentId: form.departmentId,
      isActive: form.isActive,
    });
  };

  const handleRoleChange = (user: User, role: UserRole) => {
    setUpdatingUserId(user.id);
    updateUserMutation.mutate({ id: user.id, payload: { role } });
  };

  const handleEditOpen = (user: User) => {
    setSelectedUser(user);
    setEditForm({
      firstName: user.firstName ?? '',
      lastName: user.lastName ?? '',
      displayName: user.displayName ?? '',
      role: user.role,
      departmentId: user.departmentId ?? '',
      isActive: user.isActive,
    });
    setEditOpen(true);
  };

  const handleDeleteOpen = (user: User) => {
    setSelectedUser(user);
    setDeleteOpen(true);
  };

  const handleEditSave = () => {
    if (!selectedUser) return;
    const displayName = editForm.displayName.trim() || buildDisplayName(editForm.firstName, editForm.lastName);
    updateUserMutation.mutate({
      id: selectedUser.id,
      payload: {
        firstName: editForm.firstName.trim(),
        lastName: editForm.lastName.trim(),
        displayName,
        role: editForm.role,
        departmentId: editForm.departmentId,
        isActive: editForm.isActive,
      },
    });
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            {activeTab === 0 ? 'Users' : 'Departments'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {activeTab === 0 ? 'Manage users and assign roles' : 'Manage departments and their settings'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Plus size={16} />}
          onClick={() => activeTab === 0 ? setCreateOpen(true) : setDeptCreateOpen(true)}
        >
          {activeTab === 0 ? 'Add User' : 'Add Department'}
        </Button>
      </Stack>

      <Card>
        <Tabs value={activeTab} onChange={(_, newVal) => setActiveTab(newVal)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab label="Users" />
          <Tab label="Departments" />
        </Tabs>
        <CardContent>
          {activeTab === 0 ? (
            <>
              <TextField
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                label="Search users"
                placeholder="Search by name or email"
                fullWidth
                size="small"
                sx={{ mb: 2 }}
              />

              {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>{user.displayName || buildDisplayName(user.firstName, user.lastName)}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{departmentMap.get(user.departmentId) ?? user.departmentId}</TableCell>
                      <TableCell sx={{ minWidth: 180 }}>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={user.role}
                            onChange={(event) => handleRoleChange(user, event.target.value as UserRole)}
                            disabled={updatingUserId === user.id && updateUserMutation.isPending}
                          >
                            {ROLE_OPTIONS.map((role) => (
                              <MenuItem key={role.value} value={role.value}>
                                {role.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.isActive ? 'Active' : 'Inactive'}
                          color={user.isActive ? 'success' : 'default'}
                          size="small"
                          variant={user.isActive ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Pencil size={14} />}
                            onClick={() => handleEditOpen(user)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<Trash2 size={14} />}
                            onClick={() => handleDeleteOpen(user)}
                          >
                            Delete
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {users.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No users found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
            </>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Department Name</TableCell>
                      <TableCell>Head</TableCell>
                      <TableCell>Teams</TableCell>
                      <TableCell>Members</TableCell>
                      <TableCell>Active Projects</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {departments.map((dept: any) => (
                      <TableRow key={dept.id} hover>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            {dept.color && (
                              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: dept.color }} />
                            )}
                            <Typography variant="body2" fontWeight={500}>{dept.name}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>{dept.headName || '-'}</TableCell>
                        <TableCell>{dept.teamCount || 0}</TableCell>
                        <TableCell>{dept.memberCount || 0}</TableCell>
                        <TableCell>{dept.activeProjects || 0}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Pencil size={14} />}
                              onClick={() => handleDeptEditOpen(dept)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<Trash2 size={14} />}
                              onClick={() => handleDeptDeleteOpen(dept)}
                            >
                              Delete
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                    {departments.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          No departments found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create User</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Email"
              value={form.email}
              onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Password"
              type="password"
              value={form.password}
              onChange={(event) => setForm((prev) => ({ ...prev, password: event.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="First Name"
              value={form.firstName}
              onChange={(event) => setForm((prev) => ({ ...prev, firstName: event.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Last Name"
              value={form.lastName}
              onChange={(event) => setForm((prev) => ({ ...prev, lastName: event.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Display Name"
              value={form.displayName}
              onChange={(event) => setForm((prev) => ({ ...prev, displayName: event.target.value }))}
              fullWidth
              helperText="Optional. Defaults to first and last name."
            />
            <FormControl fullWidth>
              <InputLabel id="user-role-label">Role</InputLabel>
              <Select
                labelId="user-role-label"
                label="Role"
                value={form.role}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, role: event.target.value as UserRole }))
                }
              >
                {ROLE_OPTIONS.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    {role.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel id="user-dept-label">Department</InputLabel>
              <Select
                labelId="user-dept-label"
                label="Department"
                value={form.departmentId}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, departmentId: event.target.value as string }))
                }
              >
                {departments.map((dept) => (
                  <MenuItem key={dept.id} value={dept.id}>
                    {dept.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, isActive: event.target.checked }))
                  }
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateUser}
            disabled={createUserMutation.isPending}
          >
            {createUserMutation.isPending ? 'Creating...' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="First Name"
              value={editForm.firstName}
              onChange={(event) => setEditForm((prev) => ({ ...prev, firstName: event.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Last Name"
              value={editForm.lastName}
              onChange={(event) => setEditForm((prev) => ({ ...prev, lastName: event.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Display Name"
              value={editForm.displayName}
              onChange={(event) => setEditForm((prev) => ({ ...prev, displayName: event.target.value }))}
              fullWidth
              helperText="Optional. Defaults to first and last name."
            />
            <FormControl fullWidth>
              <InputLabel id="edit-user-role-label">Role</InputLabel>
              <Select
                labelId="edit-user-role-label"
                label="Role"
                value={editForm.role}
                onChange={(event) =>
                  setEditForm((prev) => ({ ...prev, role: event.target.value as UserRole }))
                }
              >
                {ROLE_OPTIONS.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    {role.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel id="edit-user-dept-label">Department</InputLabel>
              <Select
                labelId="edit-user-dept-label"
                label="Department"
                value={editForm.departmentId}
                onChange={(event) =>
                  setEditForm((prev) => ({ ...prev, departmentId: event.target.value as string }))
                }
              >
                {departments.map((dept) => (
                  <MenuItem key={dept.id} value={dept.id}>
                    {dept.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={editForm.isActive}
                  onChange={(event) =>
                    setEditForm((prev) => ({ ...prev, isActive: event.target.checked }))
                  }
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEditSave}
            disabled={updateUserMutation.isPending}
          >
            {updateUserMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {selectedUser?.displayName || selectedUser?.email}? This will deactivate the
            user.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => selectedUser && deleteUserMutation.mutate(selectedUser.id)}
            disabled={deleteUserMutation.isPending}
          >
            {deleteUserMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Department Dialogs */}
      <Dialog open={deptCreateOpen} onClose={() => setDeptCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create Department</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Department Name"
              value={deptForm.name}
              onChange={(e) => setDeptForm(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel id="dept-head-label">Department Head</InputLabel>
              <Select
                labelId="dept-head-label"
                label="Department Head"
                value={deptForm.headId}
                onChange={(e) => setDeptForm(prev => ({ ...prev, headId: e.target.value }))}
              >
                <MenuItem value="">None</MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.displayName || user.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* Color Picker */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Department Color
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    bgcolor: deptForm.color || '#e0e0e0',
                    border: '2px solid',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.8 },
                  }}
                  onClick={(e) => {
                    setColorPickerAnchor(e.currentTarget);
                    setActiveColorField('create');
                    setCustomColorMode(false);
                  }}
                />
                <TextField
                  size="small"
                  value={deptForm.color}
                  onChange={(e) => setDeptForm(prev => ({ ...prev, color: e.target.value }))}
                  placeholder="#3f51b5"
                  sx={{ flex: 1 }}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <Button
                        size="small"
                        startIcon={<Palette size={16} />}
                        onClick={(e) => {
                          setColorPickerAnchor(e.currentTarget);
                          setActiveColorField('create');
                          setCustomColorMode(false);
                        }}
                      >
                        Pick
                      </Button>
                    ),
                  }}
                />
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeptCreateOpen(false)} color="inherit">Cancel</Button>
          <Button
            variant="contained"
            onClick={handleDeptCreate}
            disabled={createDeptMutation.isPending || !deptForm.name.trim()}
          >
            {createDeptMutation.isPending ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deptEditOpen} onClose={() => setDeptEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Department</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Department Name"
              value={deptEditForm.name}
              onChange={(e) => setDeptEditForm(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel id="dept-head-edit-label">Department Head</InputLabel>
              <Select
                labelId="dept-head-edit-label"
                label="Department Head"
                value={deptEditForm.headId}
                onChange={(e) => setDeptEditForm(prev => ({ ...prev, headId: e.target.value }))}
              >
                <MenuItem value="">None</MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.displayName || user.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* Color Picker */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Department Color
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    bgcolor: deptEditForm.color || '#e0e0e0',
                    border: '2px solid',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.8 },
                  }}
                  onClick={(e) => {
                    setColorPickerAnchor(e.currentTarget);
                    setActiveColorField('edit');
                    setCustomColorMode(false);
                  }}
                />
                <TextField
                  size="small"
                  value={deptEditForm.color}
                  onChange={(e) => setDeptEditForm(prev => ({ ...prev, color: e.target.value }))}
                  placeholder="#3f51b5"
                  sx={{ flex: 1 }}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <Button
                        size="small"
                        startIcon={<Palette size={16} />}
                        onClick={(e) => {
                          setColorPickerAnchor(e.currentTarget);
                          setActiveColorField('edit');
                          setCustomColorMode(false);
                        }}
                      >
                        Pick
                      </Button>
                    ),
                  }}
                />
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeptEditOpen(false)} color="inherit">Cancel</Button>
          <Button
            variant="contained"
            onClick={handleDeptUpdate}
            disabled={updateDeptMutation.isPending || !deptEditForm.name.trim()}
          >
            {updateDeptMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deptDeleteOpen} onClose={() => setDeptDeleteOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Delete Department</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {selectedDept?.name}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeptDeleteOpen(false)} color="inherit">Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => selectedDept && deleteDeptMutation.mutate(selectedDept.id)}
            disabled={deleteDeptMutation.isPending}
          >
            {deleteDeptMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Color Picker Popover */}
      <Popover
        open={Boolean(colorPickerAnchor)}
        anchorEl={colorPickerAnchor}
        onClose={() => {
          setColorPickerAnchor(null);
          setActiveColorField(null);
          setCustomColorMode(false);
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Paper sx={{ p: 2, width: 280 }}>
          {!customColorMode ? (
            <>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                Preset Colors
              </Typography>
              <Grid container spacing={1} sx={{ mb: 2 }}>
                {DEFAULT_COLORS.map((color) => (
                  <Grid item xs={4} key={color.hex}>
                    <Tooltip title={color.name}>
                      <Box
                        sx={{
                          width: '100%',
                          height: 50,
                          borderRadius: 1,
                          bgcolor: color.hex,
                          cursor: 'pointer',
                          border: '2px solid',
                          borderColor:
                            (activeColorField === 'create' && deptForm.color === color.hex) ||
                            (activeColorField === 'edit' && deptEditForm.color === color.hex)
                              ? 'primary.main'
                              : 'transparent',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            boxShadow: 2,
                          },
                          transition: 'all 0.2s',
                        }}
                        onClick={() => {
                          if (activeColorField === 'create') {
                            setDeptForm(prev => ({ ...prev, color: color.hex }));
                          } else if (activeColorField === 'edit') {
                            setDeptEditForm(prev => ({ ...prev, color: color.hex }));
                          }
                          setColorPickerAnchor(null);
                          setActiveColorField(null);
                        }}
                      />
                    </Tooltip>
                  </Grid>
                ))}
              </Grid>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Palette size={16} />}
                onClick={() => setCustomColorMode(true)}
              >
                Custom Color
              </Button>
            </>
          ) : (
            <>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                Custom Color
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="Hex Color"
                  value={activeColorField === 'create' ? deptForm.color : deptEditForm.color}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (activeColorField === 'create') {
                      setDeptForm(prev => ({ ...prev, color: value }));
                    } else if (activeColorField === 'edit') {
                      setDeptEditForm(prev => ({ ...prev, color: value }));
                    }
                  }}
                  placeholder="#3f51b5"
                  fullWidth
                  size="small"
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <input
                    type="color"
                    value={activeColorField === 'create' ? deptForm.color || '#3f51b5' : deptEditForm.color || '#3f51b5'}
                    onChange={(e) => {
                      if (activeColorField === 'create') {
                        setDeptForm(prev => ({ ...prev, color: e.target.value }));
                      } else if (activeColorField === 'edit') {
                        setDeptEditForm(prev => ({ ...prev, color: e.target.value }));
                      }
                    }}
                    style={{
                      width: '100%',
                      height: 80,
                      border: '2px solid #e0e0e0',
                      borderRadius: 4,
                      cursor: 'pointer',
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setCustomColorMode(false)}
                    fullWidth
                  >
                    Back
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => {
                      setColorPickerAnchor(null);
                      setActiveColorField(null);
                      setCustomColorMode(false);
                    }}
                    fullWidth
                  >
                    Done
                  </Button>
                </Box>
              </Stack>
            </>
          )}
        </Paper>
      </Popover>
    </Box>
  );
};

export default UsersPage;
